import { FC } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { faCircleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor, Badge, Group, HoverCard, Text, Tooltip } from "@mantine/core";

import { useBasePath } from "@shared/hooks";
import { Repo } from "@shared/types";

dayjs.extend(relativeTime);

export interface ProjectNameCellProps {
  repo: Repo;
}

export const ProjectNameCell: FC<ProjectNameCellProps> = ({ repo }) => {
  const basePath = useBasePath();

  return (
    <Group wrap="nowrap">
      <Text inherit>
        <Tooltip
          label={
            <Text inherit pr={6}>
              Last synced project metadata{" "}
              {dayjs(repo.scm_last_synced_at).fromNow()}
            </Text>
          }
          disabled={repo.scm_last_synced_at === null}
          withinPortal
        >
          <Text inherit span pr={6}>
            {repo.name}
          </Text>
        </Tooltip>
        {/* We should not show the warning info if a project is not yet set up since default branch information is not yet ingested */}
        {repo.is_setup && repo.primary_ref && !repo.primary_ref.id && (
          <HoverCard withArrow>
            <HoverCard.Target>
              <FontAwesomeIcon icon={faCircleExclamation} color="orange" />
            </HoverCard.Target>
            <HoverCard.Dropdown maw={300}>
              <Text fz="sm">
                No primary branch is set for this project.{" "}
                {repo.name ? (
                  <Anchor
                    href={`${basePath}/projects/${encodeURIComponent(
                      repo.name
                    )}#primary-branch`}
                    target="_blank"
                    rel="noopener noreferrer"
                    inherit
                  >
                    Set the primary branch
                  </Anchor>
                ) : (
                  // Theorically repo.name should always be there, but handling this just in case
                  <Text inherit span td="underline">
                    Set the primary branch
                  </Text>
                )}{" "}
                so it appears in findings and reporting filters as expected.
              </Text>
            </HoverCard.Dropdown>
          </HoverCard>
        )}
      </Text>
      {repo.is_archived && (
        <Tooltip label="GitHub has marked this repository as archived">
          <Badge ml="md" variant="outline" color="gray">
            Archived
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
};
